import styled from 'styled-components'
import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { MapComponent, Marker } from '../gmaps/map.component'
import { SelectedPhamarcyPin } from './selected-pharmacy.pin'
import { MePin } from './me.pin'
import { PharmacyOrder } from '@/dto/api/prescription.dto'
import { ActionButton } from '@/components/ui/custom/action-button'
import { Button } from '@/components/ui/button'
import { LudaPhamarcyPin } from '@/components/dialog/luda-pharmacy.pin'
import { ExternalPhamarcyPin } from '@/components/dialog/external-pharmacy.pin'
import { trackEvent } from '@/services/analytics.service'

interface Props {
  open: boolean
  onClose: () => void
  onChange: (pharmacy: PharmacyOrder) => void
  pharmacies: PharmacyOrder[]
  selectedPharmacy: PharmacyOrder | null
  address: google.maps.places.PlaceResult | null
}

const PharmacyDialogStyle = styled.section`
  .pharmacy-items {
    overflow-y: auto;

    @media (min-width: 500px) {
      margin: 0 1rem;
    }

    .pharmacy-item {
      cursor: pointer;
      border: 1px solid #1e742a;
      border-radius: 8px;
      color: #14571e;
      padding: 16px;
      margin-bottom: 16px
    }

    .is-external {
      border: 1px solid #e5e5e5;
      color: #000;
    }

    .no-pharmacies {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }

    .active {
      background-color: #f5f5f5;
      font-weight: bold;
    }

    .section-divider {
      border-top: 2px solid #e5e5e5;
      margin: 16px 0;
      padding-top: 8px;
    }

    .external-header {
      font-size: 12px;
      font-weight: 600;
      color: #666;
      margin-bottom: 8px;
    }
  }

  .see-more-container {
    text-align: center;
    margin-bottom: 16px;
  }

  button[type='submit'] {
    margin-top: 16px;
  }
`

const getPharmacyPin = (pharmacy: PharmacyOrder): JSX.Element => {
  if (pharmacy.selected) {
    return <SelectedPhamarcyPin />
  }

  if (pharmacy.pharmacy.isExternal) {
    return <ExternalPhamarcyPin />
  }

  return <LudaPhamarcyPin />
}

export function PharmacySelectorDialogComponent ({ open, onClose, address, pharmacies, onChange, selectedPharmacy }: Props): JSX.Element {
  const [showExternal, setShowExternal] = useState(false)

  const internalPharmacies = pharmacies.filter((p) => !p.pharmacy.isExternal)
  const externalPharmacies = pharmacies.filter((p) => p.pharmacy.isExternal)
  const hasInternalPharmacies = internalPharmacies.length > 0
  const hasExternalPharmacies = externalPharmacies.length > 0

  // Always show internal pharmacies first, then add external if showExternal is true
  const pharmaciesToShow = !hasInternalPharmacies
    ? pharmacies // if no internal, show all (which are external)
    : showExternal
      ? [...internalPharmacies, ...externalPharmacies] // internal + external
      : internalPharmacies // only internal
  const markers: Marker[] = pharmaciesToShow.map((pharmacy) => ({
    lat: pharmacy.pharmacy?.coordinates.lat,
    lng: pharmacy.pharmacy?.coordinates.lng,
    title: pharmacy.pharmacy.description,
    infoWindow: (
      <div>
        <h3>{pharmacy.pharmacy?.description}</h3>
        <p>{pharmacy.pharmacy?.address}</p>
      </div>
    ),
    pin: getPharmacyPin(pharmacy)
  }))

  if (address?.geometry?.location?.lat != null && address.geometry?.location?.lng != null) {
    markers.push({
      lat: address.geometry.location.lat(),
      lng: address.geometry.location.lng(),
      title: 'Tu ubicación',
      pin: <MePin />
    })
  }

  // Cerrar por la X no es lo mismo que confirmar: se trackean por separado.
  const handleDismiss = (): void => {
    trackEvent('pharmacy_selector_dismissed', {
      pharmacy_id: selectedPharmacy?.pharmacy.id,
      is_external: selectedPharmacy?.pharmacy.isExternal,
      pharmacies_shown: pharmaciesToShow.length
    })
    onClose()
  }

  const center = {
    lat: selectedPharmacy?.pharmacy.coordinates.lat ?? address?.geometry?.location?.lat() ?? pharmacies[0]?.pharmacy.coordinates.lat,
    lng: selectedPharmacy?.pharmacy.coordinates.lng ?? address?.geometry?.location?.lng() ?? pharmacies[0]?.pharmacy.coordinates.lng
  }

  return (
    <Dialog open={open}>
      <DialogContent className='min-w-[95%] min-h-[95%] max-h-[95%] max-w-[95%]' onClose={handleDismiss}>
        <PharmacyDialogStyle>
          <DialogHeader className='flex flex-col items-start justify-between'>
            <DialogTitle>
              Selecciona tu farmacia
            </DialogTitle>
            <DialogDescription>
              Selecciona tu farmacia más cercana
            </DialogDescription>
          </DialogHeader>
          <div className='grid grid-flow-row-dense grid-cols-1 md:grid-cols-3 h-xl'>
            <div className='col-span-1 md:col-span-2 md:h-[80vh] h-[30vh] mb-10 md:mb-0'>
              <MapComponent
                key={`map-with-selected-pharmacy-${selectedPharmacy?.pharmacy.id ?? 'none'}`}
                center={center}
                mapId='pharmacy-selector-map'
                markers={markers}
              />
            </div>
            <div className='pharmacy-items md:h-[80vh] h-[45vh]'>
              {pharmaciesToShow.map((pharmacy, index) => {
                const isCurrentExternal = pharmacy.pharmacy.isExternal
                const isPreviousInternal = index > 0 && !pharmaciesToShow[index - 1].pharmacy.isExternal
                const shouldShowDivider = isPreviousInternal && isCurrentExternal
                return (
                  <div key={pharmacy.pharmacy.description}>
                    {shouldShowDivider && (
                      <>
                        <div className='section-divider' />
                        <div className='external-header'>Farmacias externas:</div>
                      </>
                    )}
                    <div
                      className={`pharmacy-item ${pharmacy.pharmacy.id === selectedPharmacy?.pharmacy.id ? 'active' : ''} ${pharmacy.pharmacy.isExternal ? 'is-external' : ''}`}
                      onClick={() => onChange(pharmacy)}
                    >
                      {pharmacy.pharmacy.description}
                    </div>
                  </div>
                )
              })}

              {hasInternalPharmacies && hasExternalPharmacies && !showExternal && (
                <div className='see-more-container'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => {
                      trackEvent('external_pharmacies_shown', {
                        external_pharmacies: externalPharmacies.length,
                        internal_pharmacies: internalPharmacies.length
                      })
                      setShowExternal(true)
                    }}
                  >
                    Ver más farmacias
                  </Button>
                </div>
              )}

              {pharmaciesToShow.length === 0 && (
                <div className='no-pharmacies'>
                  No hay farmacias disponibles
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <ActionButton
              type='submit'
              onClick={() => {
                trackEvent('pharmacy_selector_confirm', {
                  pharmacy_id: selectedPharmacy?.pharmacy.id,
                  is_external: selectedPharmacy?.pharmacy.isExternal,
                  pharmacies_shown: pharmaciesToShow.length
                })
                onClose()
              }}
              disabled={pharmaciesToShow.length === 0}
            >
              Cambiar farmacia
            </ActionButton>
          </DialogFooter>
        </PharmacyDialogStyle>
      </DialogContent>
    </Dialog>
  )
}
